import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import * as Yup from 'yup';
import { Box, Button, Card, Flex, Label, Paragraph, Text, useColorMode } from 'theme-ui';
import timerIcon from '../../../assets/images/Icon/EstimatedIcon.svg';
import { msToTime } from '../../../../utils/GenerateQueryString';
import { useFormik } from 'formik';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Inputform } from '../../../forms-component/InputForm';
import { estimatedReason } from '../../../../utils/constent';
import FormSelect from '../../../forms-component/globalSelect';
import { changeEstimationTime } from '../../../../store/actions/project.action';
import toast from 'react-hot-toast';
import { useState, useEffect } from 'react';
import { FaAngleDown, FaAngleUp } from 'react-icons/fa';
import { TimeEstimateHistory } from '../TimeEstimateHistory';
import theme from '../../../../theme';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../../@types';

export const EstimatedTimeModal = ({ estimatedOpen, estimatedClose, timeEstimate, isTracking, timeTaken }: any) => {
  const { hours: hoursEstimate, minutes: minutesEstimate, seconds: secondsEstimate } = msToTime(timeEstimate);
  const { taskId } = useParams<{ taskId: string }>();
  const [showHistory, setShowHistory] = useState<boolean>(false);
  const [tradeNameRef, setTradeNameRef] = useState<string>("");
  // immediate validation error for minutes input while typing
  const [minValueImmediateError, setMinValueImmediateError] = useState<string | null>(null);
  const timeSpent = msToTime(timeTaken);
  const { auther } = useSelector((state: RootState) => state.auth);


  useEffect(() => {
    if (!estimatedOpen) {
      setShowHistory(false);
    }
  }, [estimatedOpen]);

  const hoursToMilliseconds = (hours: any, minutes: any) => {
    return (hours * 3600 + minutes * 60) * 1000;
  }
  const allowedRoles = ["Project Manager", "Project Worker", "Super Admin", "Region Admin"];
  const restrictedOptions = ["BC (xrefs)", "PS (xrefs)", "External Client Meeting"];
  const filteredEstimatedReason = estimatedReason.filter((option) => {
    const isRestrictedOption = restrictedOptions.includes(option.label);

    // Check if user has any allowed role
    const hasAllowedRole = auther?.Role?.some((role: any) =>
      allowedRoles.includes(role.RoleName)
    );

    if (isRestrictedOption) {
      return hasAllowedRole;
    }

    return true; // Include non-restricted options
  });
  const dispatch = useDispatch();
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      description: "",
      reasonType: "",
      hoursValue: +hoursEstimate || 0,
      minValue: +minutesEstimate || 0,
    },
    validationSchema: Yup.object({
      description: Yup.string().required('Reason is required'),
      reasonType: Yup.string().required('Reason Type is required'),
      hoursValue: Yup.string().matches(
        /(?=.*?\d)^\$?(([1-9]\d{0,2}(,\d{3})*)|\d+)?(\.\d{1,2})?$/,
        "not valid"
      ),
      minValue: Yup.number().max(60, "Maximum value is 60")
    }),
    onSubmit: (value) => {
      const newTime = hoursToMilliseconds(value.hoursValue, value.minValue);
      const timeSpent = msToTime(timeTaken);

      const spentTime = `${timeSpent?.hours}:${timeSpent?.minutes}:${timeSpent?.seconds?.toString().padStart(2, '0')}`

      if (newTime <= timeTaken) {
        toast.error(`New estimated time must be greater than the time already spent.Your spent time is ${spentTime}`);
        return;
      }
      const payload = {
        newTime: hoursToMilliseconds(value.hoursValue, value.minValue),
        description: value.description,
        reasonType: value.reasonType,
      };

      dispatch(changeEstimationTime(payload, taskId));
      estimatedClose();
    },
  });

  const { handleSubmit, setFieldValue, values, handleBlur, touched, errors } = formik;

  const estimateTimeHandler = () => {
    if (isTracking) {
      toast.dismiss();
      toast.error("Time tracking in progress, So manage estimated time are disabled.", {
        duration: 4000,
      });
    }
  };
  const [colorMode] = useColorMode();
  const modalHeader = colorMode === 'light' ? "#fff" : "#20283c";
  const titleHeader = colorMode === 'light' ? "#0a0a0a" : "#dadada";
  const closeButton = (
    <button
      type="button"
      aria-label="Close"
      onClick={estimatedClose}
      style={{
        color: colorMode === 'dark' ? '#fff' : '#000',
        background: 'transparent',
        border: 'none',
        fontSize: 23,
        lineHeight: 1,
        padding: 0,
        cursor: 'pointer'
      }}
    >
      <span aria-hidden="true">&times;</span>
    </button>
  );
  return (
    <Modal isOpen={estimatedOpen} centered size='lg' toggle={estimatedClose} >
      <ModalHeader toggle={estimatedClose} close={closeButton} style={{ backgroundColor: modalHeader }}>
        <Paragraph>
          <img src={timerIcon} alt='' className='mx-2' style={{ filter: colorMode === 'dark' ? 'invert(1) grayscale(100%) brightness(200%)' : 'none' }} />
          <Text sx={{ color: titleHeader }}>Manage Estimated time</Text>
        </Paragraph>
      </ModalHeader>
      <form onSubmit={handleSubmit}>
        <ModalBody className='scrollable-modal-body text-center' style={{ backgroundColor: modalHeader }}>
          <Card sx={{ boxShadow: "none", border: `${theme?.colors?.lightbackground}`, borderRadius: "12px", padding: "12px 20px", backgroundColor: modalHeader }}>
            <Text sx={{ color: `${theme.colors?.muted}` }}>Estimated Time</Text>
            <Paragraph sx={{ fontSize: "32px", fontWeight: "400", mt: "1.5rem", color: titleHeader }}>{`${hoursEstimate || "00"}:${minutesEstimate || "00"}:${secondsEstimate || "00"}`}</Paragraph>
            <Text sx={{ color: `${theme.colors?.muted}`, fontSize: "10px" }}>{`${hoursEstimate || "00"} Hours ${minutesEstimate || "00"} Minutes ${secondsEstimate || "00"} seconds`}</Text>
            <Flex sx={{ gap: "1rem", alignItems: "center", justifyContent: "center", mt: "1.5rem" }}>
              <Button
                type='button'
                variant='startButton'
                disabled={(values.minValue === 0 && values.hoursValue === 0)}
                sx={{ borderColor: titleHeader, color: titleHeader, padding: "8px 12px 8px 12px", background: colorMode === "light" ? "#fff" : "#20283c" }}
                onClick={() => {
                  // compute new minute value safely and clamp to 0..60
                  let newMin = 0;
                  if (Number(values.minValue) === 30) {
                    newMin = 0;
                  } else if (Number(values.minValue) === 0) {
                    if (Number(values.hoursValue) > 0) {
                      setFieldValue("hoursValue", Number(values.hoursValue) - 1);
                    }
                    newMin = Number(values.minValue) + 30;
                  } else {
                    newMin = Number(values.minValue) - 30;
                  }
                  newMin = Math.max(0, Math.min(60, Number(newMin)));
                  setFieldValue("minValue", newMin);
                }}>
                - 30 min <Text sx={{ color: `${theme?.colors?.primary}` }}>Subtract</Text>
              </Button>
              <Inputform
                label={""}
                Placeholder={`${hoursEstimate}`}
                Name={"hoursValue"}
                touched={touched?.hoursValue}
                errors={errors?.hoursValue}
                values={values.hoursValue}
                onBlur={handleBlur}
                type='number'
                sx={{ width: "65px", color: titleHeader }}
                OnChange={(event: any) => {
                  const hoursValue = event.target.value;
                  setFieldValue("hoursValue", hoursValue, true);
                }}
              />:
              <Inputform
                label={""}
                Placeholder={`${minutesEstimate}`}
                Name={"minValue"}
                type='number'
                // touched={touched?.minValue}
                // errors={errors?.minValue}
                values={values.minValue}
                onBlur={handleBlur}
                sx={{ width: "65px", color: titleHeader }}
                OnChange={(event: any) => {
                  const minValueRaw = event.target.value;
                  // allow empty
                  if (minValueRaw === '' || minValueRaw === null) {
                    setMinValueImmediateError(null);
                    setFieldValue("minValue", minValueRaw, true);
                    return;
                  }
                  const minValueNum = Number(minValueRaw);
                  if (Number.isNaN(minValueNum)) {
                    setMinValueImmediateError(null);
                    setFieldValue("minValue", minValueRaw, true);
                    return;
                  }
                  if (minValueNum > 60) {
                    // reject change and show immediate error
                    setMinValueImmediateError('Maximum value is 60');
                    return;
                  }
                  setMinValueImmediateError(null);
                  setFieldValue("minValue", minValueNum, true);
                }}
              />
              <Button
                type='button'
                variant='startButton'
                sx={{ borderColor: titleHeader, color: titleHeader, padding: "8px 12px 8px 12px", background: colorMode === "light" ? "#fff" : "#20283c" }}
                onClick={() => {
                  // add 30 minutes but clamp to max 60
                  if (Number(values.minValue) === 30) {
                    setFieldValue("hoursValue", Number(values.hoursValue) + 1);
                    setFieldValue("minValue", 0);
                  } else {
                    const computed = Number(values.minValue) + 30;
                    const newMin = Math.max(0, Math.min(60, computed));
                    setFieldValue("minValue", newMin);
                  }
                }}>
                + 30 min <Text sx={{ color: `${theme?.colors?.primary}` }}>Extend</Text>

              </Button>

            </Flex>
            <Box>
              {minValueImmediateError ? (
                <Text variant="Inter15Regular30lh" className="text-danger" sx={{ my: "5px", minHeight: "12px", lineHeight: '10px' }}>
                  {minValueImmediateError}
                </Text>
              ) : ((errors.minValue && touched.minValue) && (
                <Text variant="Inter15Regular30lh" className="text-danger" sx={{ my: "5px", minHeight: "12px", lineHeight: '10px' }}>
                  {errors.minValue.toString()}
                </Text>
              ))}
            </Box>
            <Box sx={{ mt: "1rem", textAlign: "start" }}>
              <Label sx={{ fontSize: '14px', fontWeight: 500, color: titleHeader }} className={`form-label mt-1 ${errors.reasonType && touched.reasonType ? 'is-invalid' : ''}`}>
                Reason Type<span className="mx-1 text-danger">*</span>
              </Label>
              <FormSelect
                value={values?.reasonType}
                placeholder={"Select Reason Type"}
                options={filteredEstimatedReason || []}
                isMulti={false}
                touched={touched?.reasonType}
                errors={errors?.reasonType}
                onBlur={handleBlur}
                Name='reasonType'
                onChange={(values: any) => {
                  setFieldValue("reasonType", values?.value, true);
                }}
              />
            </Box>
            <Box sx={{ mt: "1rem", textAlign: "start" }}>
              <Inputform
                label={"Enter the Reason"}
                sx={{ color: titleHeader }}
                Placeholder={`Enter the reason...`}
                Name={"description"}
                touched={touched?.description}
                errors={errors?.description}
                asterisk={true}
                values={values.description}
                onBlur={handleBlur}
                OnChange={(event: any) => {
                  const description = event.target.value;
                  setFieldValue("description", description, true);
                }}
              />
            </Box>
          </Card>
        </ModalBody>
        <ModalFooter className='gap-2 justify-content-end p-3' style={{ backgroundColor: modalHeader }} >
          <Button variant='SaveButton' type={isTracking ? 'button' : 'submit'} onClick={estimateTimeHandler}>Send</Button>
          <Flex
            sx={{
              border: `1px solid ${theme.colors?.primary}`,
              borderRadius: "12px",
              mx: "1rem",
              mt: 2,
              padding: "14px",
              cursor: 'pointer',
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}
            onClick={() => setShowHistory(!showHistory)}>
            <Text sx={{ color: titleHeader }}>History</Text>{showHistory ? <FaAngleUp style={{ color: titleHeader }} className='text-end' /> : <FaAngleDown style={{ color: titleHeader }} className='text-end' />}
          </Flex>
        </ModalFooter>
      </form>
      {showHistory && (
        <>
          <h5 className='ps-4 mt-2'>Time Estimate History</h5>
          <TimeEstimateHistory tradeNameRef={tradeNameRef} showHistory={showHistory} />
        </>
      )}
    </Modal>
  );
};
